/**
 * CurriculumSelector
 *
 * Displays the 10 busiest operational days derived from the DACAMS analysis
 * and allows the HITL trainer to select which days (and their moments) to
 * include in the AI training curriculum.
 *
 * Data source: GET /projects/{projectId}
 *   → analysis_results.advanced_analysis.top10_busiest_days.days[]
 *
 * Save target: PATCH /ai-training/sessions/{sessionId}
 *   → { training_days: string[] }
 */

import React, { useState, useCallback } from 'react';
import {
    CalendarDots,
    AirplaneLanding,
    AirplaneTakeoff,
    Clock,
    CheckSquare,
    Square,
    FloppyDisk,
    SpinnerGap,
    Warning,
    CheckCircle,
    ArrowClockwise,
} from '@phosphor-icons/react';
import api from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CurriculumDay {
    date: string;
    total: number;
    arrivals: number;
    departures: number;
    rank: number;
    /** Derived client-side from day-of-week (placeholder when no hourly data). */
    moments_of_truth: {
        peak_arrivals_hour: number | null;
        peak_departures_hour: number | null;
        valley_hour: number | null;
    };
}

interface CurriculumSelectorProps {
    projectId: string;
    sessionId: string;
    /** Called after a successful save so the parent can update session state. */
    onSaved?: (selectedDates: string[]) => void;
    /** Pre-selected dates (e.g. from session.training_days). */
    initialSelectedDates?: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a date string (YYYY-MM-DD) to a locale-friendly label. */
function formatDate(dateStr: string): string {
    try {
        const d = new Date(dateStr + 'T12:00:00Z');
        return d.toLocaleDateString('es-ES', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

/** Format an hour number (0–23) as HH:00. */
function fmtHour(h: number | null): string {
    if (h === null) return '--:--';
    return `${String(h).padStart(2, '0')}:00`;
}

/**
 * Derive approximate Moments of Truth from the arrival / departure counts
 * when no hourly breakdown is available. Uses the ratio of ARR/DEP as a
 * heuristic to guess peak windows typical for a busy day.
 */
function deriveMoments(arrivals: number, departures: number): CurriculumDay['moments_of_truth'] {
    // Without hourly data we cannot compute real peaks; return null so the UI
    // renders "N/D" rather than fabricated values.
    return {
        peak_arrivals_hour: null,
        peak_departures_hour: null,
        valley_hour: null,
    };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
    const isTop3 = rank <= 3;
    return (
        <span
            className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                isTop3
                    ? 'bg-[var(--aipron-accent)] text-white'
                    : 'bg-[var(--surface)] border border-[var(--border)] text-[var(--text-secondary)]'
            }`}
        >
            {rank}
        </span>
    );
};

const MomentBadge: React.FC<{ label: string; hour: number | null; color: string }> = ({
    label,
    hour,
    color,
}) => (
    <span
        className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium ${color}`}
    >
        <Clock size={10} weight="bold" />
        {label} {fmtHour(hour)}
    </span>
);

// ---------------------------------------------------------------------------
// Day card
// ---------------------------------------------------------------------------

const DayCard: React.FC<{
    day: CurriculumDay;
    selected: boolean;
    onToggle: () => void;
}> = ({ day, selected, onToggle }) => {
    const isTop3 = day.rank <= 3;

    return (
        <div
            onClick={onToggle}
            className={`relative cursor-pointer rounded-lg border p-3 transition-all ${
                selected
                    ? isTop3
                        ? 'border-[var(--aipron-accent)] bg-[var(--aipron-accent)]/8'
                        : 'border-[var(--aipron-accent)]/60 bg-[var(--surface)]'
                    : 'border-[var(--border)] bg-[var(--surface)] opacity-60 hover:opacity-100'
            }`}
        >
            {/* Top row */}
            <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                    <RankBadge rank={day.rank} />
                    <span className="text-xs text-[var(--text-secondary)]">{formatDate(day.date)}</span>
                </div>
                {selected ? (
                    <CheckSquare size={18} weight="fill" className="flex-shrink-0 text-[var(--aipron-accent)]" />
                ) : (
                    <Square size={18} className="flex-shrink-0 text-[var(--text-secondary)]" />
                )}
            </div>

            {/* Total movements */}
            <div className="mb-1 text-2xl font-bold text-[var(--text-primary)]">
                {day.total.toLocaleString()}
            </div>
            <div className="mb-2 text-[10px] text-[var(--text-secondary)] uppercase tracking-wide">
                movimientos totales
            </div>

            {/* ARR / DEP split */}
            <div className="mb-3 flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-[var(--aipron-accent)]">
                    <AirplaneLanding size={12} weight="bold" />
                    {day.arrivals} ARR
                </span>
                <span className="flex items-center gap-1 text-emerald-500">
                    <AirplaneTakeoff size={12} weight="bold" />
                    {day.departures} DEP
                </span>
            </div>

            {/* Moments of Truth */}
            <div className="flex flex-wrap gap-1">
                <MomentBadge
                    label="Pico ARR"
                    hour={day.moments_of_truth.peak_arrivals_hour}
                    color="bg-[var(--aipron-accent)]/15 text-[var(--aipron-accent)]"
                />
                <MomentBadge
                    label="Pico DEP"
                    hour={day.moments_of_truth.peak_departures_hour}
                    color="bg-emerald-500/15 text-emerald-400"
                />
                <MomentBadge
                    label="Valle"
                    hour={day.moments_of_truth.valley_hour}
                    color="bg-[var(--surface)] text-[var(--text-secondary)] border border-[var(--border)]"
                />
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CurriculumSelector: React.FC<CurriculumSelectorProps> = ({
    projectId,
    sessionId,
    onSaved,
    initialSelectedDates = [],
}) => {
    const [days, setDays] = useState<CurriculumDay[]>([]);
    const [selectedDates, setSelectedDates] = useState<Set<string>>(
        new Set(initialSelectedDates),
    );
    const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [isCollapsed, setIsCollapsed] = useState(false);

    // -----------------------------------------------------------------------
    // Load curriculum from project analysis_results
    // -----------------------------------------------------------------------

    const loadCurriculum = useCallback(async () => {
        if (!projectId) return;
        setLoadState('loading');
        setLoadError('');

        try {
            const resp = await api.get(`/projects/${projectId}`);
            const project = resp.data;

            const top10 =
                project?.analysis_results?.advanced_analysis?.top10_busiest_days?.days ?? [];

            if (!Array.isArray(top10) || top10.length === 0) {
                setLoadError(
                    'No se encontraron días de entrenamiento en el análisis del proyecto. Ejecute el análisis DACAMS primero.',
                );
                setLoadState('error');
                return;
            }

            const curriculumDays: CurriculumDay[] = top10
                .slice(0, 10)
                .map((d: { date: string; total: number; arrivals: number; departures: number }, idx: number) => ({
                    date: d.date,
                    total: d.total ?? 0,
                    arrivals: d.arrivals ?? 0,
                    departures: d.departures ?? 0,
                    rank: idx + 1,
                    moments_of_truth: deriveMoments(d.arrivals ?? 0, d.departures ?? 0),
                }));

            setDays(curriculumDays);

            // Pre-select all days if none were previously set
            if (initialSelectedDates.length === 0) {
                setSelectedDates(new Set(curriculumDays.map((d) => d.date)));
            }

            setLoadState('loaded');
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Error al cargar el proyecto';
            setLoadError(message);
            setLoadState('error');
        }
    }, [projectId, initialSelectedDates]);

    // -----------------------------------------------------------------------
    // Toggle day selection
    // -----------------------------------------------------------------------

    const toggleDay = (date: string) => {
        setSelectedDates((prev) => {
            const next = new Set(prev);
            if (next.has(date)) {
                next.delete(date);
            } else {
                next.add(date);
            }
            return next;
        });
        // Reset saved state on any change
        if (saveState === 'saved') setSaveState('idle');
    };

    const selectAll = () => setSelectedDates(new Set(days.map((d) => d.date)));
    const selectNone = () => setSelectedDates(new Set());

    // -----------------------------------------------------------------------
    // Save curriculum
    // -----------------------------------------------------------------------

    const saveCurriculum = async () => {
        if (!sessionId) return;
        setSaveState('saving');
        setSaveError('');

        try {
            const training_days = Array.from(selectedDates).sort();
            await api.patch(`/ai-training/sessions/${sessionId}`, { training_days });
            setSaveState('saved');
            onSaved?.(training_days);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Error al guardar el curriculum';
            setSaveError(message);
            setSaveState('error');
        }
    };

    // -----------------------------------------------------------------------
    // Derived state
    // -----------------------------------------------------------------------

    const selectedDays = days.filter((d) => selectedDates.has(d.date));
    const totalSelectedMovements = selectedDays.reduce((sum, d) => sum + d.total, 0);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
            {/* Header */}
            <div
                className="flex cursor-pointer items-center justify-between px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--surface)] select-none"
                onClick={() => setIsCollapsed((p) => !p)}
            >
                <div className="flex items-center gap-2">
                    <CalendarDots size={18} weight="duotone" className="text-[var(--aipron-accent)]" />
                    <div>
                        <span className="text-sm font-semibold text-[var(--text-primary)]">
                            Curriculum de Entrenamiento
                        </span>
                        {loadState === 'loaded' && (
                            <span className="ml-2 text-xs text-[var(--text-secondary)]">
                                {selectedDates.size}/{days.length} días seleccionados
                            </span>
                        )}
                    </div>
                </div>
                <span className="text-xs text-[var(--text-secondary)]">
                    {isCollapsed ? 'Expandir' : 'Colapsar'}
                </span>
            </div>

            {!isCollapsed && (
                <div className="p-4 space-y-4">
                    {/* Description + load button */}
                    <div className="flex items-start justify-between gap-4">
                        <p className="text-xs text-[var(--text-secondary)] leading-relaxed max-w-lg">
                            Los 10 días con mayor trafico operacional del período analizado por DACAMS.
                            Selecciona los días que el modelo de IA utilizará como escenarios de entrenamiento.
                        </p>
                        <button
                            onClick={loadCurriculum}
                            disabled={loadState === 'loading'}
                            className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--text-primary)] hover:border-[var(--aipron-accent)] hover:text-[var(--aipron-accent)] disabled:opacity-50 transition-colors"
                        >
                            {loadState === 'loading' ? (
                                <SpinnerGap size={14} className="animate-spin" />
                            ) : (
                                <ArrowClockwise size={14} />
                            )}
                            {loadState === 'loading' ? 'Cargando...' : 'Cargar Curriculum'}
                        </button>
                    </div>

                    {/* Error state */}
                    {loadState === 'error' && (
                        <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                            <Warning size={14} className="flex-shrink-0 mt-0.5" />
                            <span>{loadError}</span>
                        </div>
                    )}

                    {/* Days grid */}
                    {loadState === 'loaded' && days.length > 0 && (
                        <>
                            {/* Select all / none */}
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={selectAll}
                                    className="text-xs text-[var(--aipron-accent)] hover:underline"
                                >
                                    Seleccionar todos
                                </button>
                                <span className="text-[var(--border)]">|</span>
                                <button
                                    onClick={selectNone}
                                    className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
                                >
                                    Deseleccionar todos
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-1 lg:grid-cols-2">
                                {days.map((day) => (
                                    <DayCard
                                        key={day.date}
                                        day={day}
                                        selected={selectedDates.has(day.date)}
                                        onToggle={() => toggleDay(day.date)}
                                    />
                                ))}
                            </div>

                            {/* Moments of Truth summary */}
                            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg)] p-3">
                                <div className="mb-2 text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
                                    Resumen de Momentos de Verdad seleccionados
                                </div>
                                {selectedDays.length === 0 ? (
                                    <p className="text-xs text-[var(--text-secondary)] italic">
                                        Sin días seleccionados.
                                    </p>
                                ) : (
                                    <div className="space-y-1">
                                        {selectedDays.map((d) => (
                                            <div
                                                key={d.date}
                                                className="flex items-center gap-2 text-xs text-[var(--text-secondary)]"
                                            >
                                                <span className="w-24 text-[var(--text-primary)] font-mono">
                                                    {d.date}
                                                </span>
                                                <span className="flex items-center gap-1 text-[var(--aipron-accent)]">
                                                    <AirplaneLanding size={10} />
                                                    {d.arrivals}
                                                </span>
                                                <span className="flex items-center gap-1 text-emerald-500">
                                                    <AirplaneTakeoff size={10} />
                                                    {d.departures}
                                                </span>
                                                <span className="ml-auto text-[var(--text-secondary)]">
                                                    {d.total} mvts
                                                </span>
                                            </div>
                                        ))}
                                        <div className="mt-2 border-t border-[var(--border)] pt-2 flex items-center justify-between text-xs">
                                            <span className="text-[var(--text-secondary)]">
                                                Total movimientos de entrenamiento
                                            </span>
                                            <span className="font-bold text-[var(--text-primary)]">
                                                {totalSelectedMovements.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Save button */}
                            <div className="flex items-center justify-between gap-3">
                                {saveState === 'saved' && (
                                    <span className="flex items-center gap-1.5 text-xs text-emerald-500">
                                        <CheckCircle size={14} weight="fill" />
                                        Curriculum guardado. {selectedDates.size} días de entrenamiento seleccionados.
                                    </span>
                                )}
                                {saveState === 'error' && (
                                    <span className="flex items-center gap-1.5 text-xs text-red-400">
                                        <Warning size={14} />
                                        {saveError}
                                    </span>
                                )}
                                {(saveState === 'idle' || saveState === 'saving') && (
                                    <span />
                                )}

                                <button
                                    onClick={saveCurriculum}
                                    disabled={saveState === 'saving' || selectedDates.size === 0}
                                    className="ml-auto flex items-center gap-1.5 rounded-lg bg-[var(--aipron-accent)] px-4 py-2 text-xs font-semibold text-white hover:bg-[#d14f1f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {saveState === 'saving' ? (
                                        <SpinnerGap size={14} className="animate-spin" />
                                    ) : (
                                        <FloppyDisk size={14} weight="bold" />
                                    )}
                                    {saveState === 'saving' ? 'Guardando...' : 'Guardar Curriculum'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CurriculumSelector;
